import upperFirst from 'lodash/upperFirst'
import NavigationService from '@disco/state-management/NavigationService'
import type { EmailCampaign, EmailCampaignId } from 'types/campaign'
import type { Thunk } from 'types/redux'
import type { CampaignSection } from 'types/content-editor'
import { loadCampaignFooters } from '@disco/state-management/actions/campaign-footer-actions'
import { waitForPendingSave } from '@disco/state-management/actions/campaign-details-actions'
import mixpanel from '@disco/api/mixpanel'
import api from '@disco/api/server'
import {
  CAMPAIGN_DETAILS_RESOLVE_CONFLICT,
  CAMPAIGN_LIST_BEGIN_CREATING,
  CAMPAIGN_LIST_BEGIN_EDIT,
  CAMPAIGN_LIST_CANCEL_CREATING,
  CAMPAIGN_LIST_CREATE,
  CAMPAIGN_LIST_CREATE_FAIL,
  CAMPAIGN_LIST_CREATE_SUCCESS,
  CAMPAIGN_LIST_DELETE,
  CAMPAIGN_LIST_DELETE_FAIL,
  CAMPAIGN_LIST_DELETE_SUCCESS,
  CAMPAIGN_LIST_FILTER,
  CAMPAIGN_LIST_GET,
  CAMPAIGN_LIST_GET_ALL,
  CAMPAIGN_LIST_GET_ALL_FAIL,
  CAMPAIGN_LIST_GET_ALL_SUCCESS,
  CAMPAIGN_LIST_GET_FAIL,
  CAMPAIGN_LIST_GET_SUCCESS,
  CAMPAIGN_SEARCH,
} from '@disco/constants/ActionTypes'
import {
  getCampaign,
  getCampaignLink,
} from '@disco/state-management/selectors/campaigns'
import captureError, { parseServerError } from '@disco/utils/captureError'
import { defaultCampaignTemplate } from '@disco/content-editor/default'
import {
  getRecipientFilter,
  getTemplateData,
} from '@disco/utils/emailCampaigns'
import { pushUINotification } from '@disco/utils/notifications'

export const fetchAllCampaigns = async (): Promise<EmailCampaign[]> => {
  let response = await api.getUrl<{
    results: EmailCampaign[]
    next: string | null | undefined
  }>(`${api.host}/api/campaign/`, { page_size: 4000 }, 'api.getCampaigns')
  const campaigns = [...response.results]
  while (response.next) {
    response = await api.getUrl(response.next, null, 'api.getCampaigns')
    campaigns.push(...response.results)
  }
  return campaigns
}

// module-level so the campaign list can wait on a sync started from the contacts pages
let pendingCampaignRecipientSync: Promise<void> = Promise.resolve()
const CAMPAIGN_RECIPIENT_SYNC_WAIT_MS = 5000

export const queueCampaignRecipientSync = (
  run: () => Promise<void>,
): Promise<void> => {
  // a rejected run would otherwise skip every later sync and reject the campaign list's wait
  pendingCampaignRecipientSync = pendingCampaignRecipientSync
    .then(run)
    .catch(error => {
      captureError(error, {
        description: 'Error in queueCampaignRecipientSync()',
      })
    })
  return pendingCampaignRecipientSync
}

// capped because the sync may be loading every contact, and the campaign list must not hang on it
export const waitForCampaignRecipientSync = async (): Promise<void> => {
  let timeoutId: ReturnType<typeof setTimeout> | undefined
  await Promise.race([
    pendingCampaignRecipientSync,
    new Promise<void>(resolve => {
      timeoutId = setTimeout(resolve, CAMPAIGN_RECIPIENT_SYNC_WAIT_MS)
    }),
  ])
  clearTimeout(timeoutId)
}

/**
 * Loads all campaigns from the server.
 * @returns Thunk that fetches all campaigns
 */
export function campaignListGetAll(): Thunk {
  return async dispatch => {
    dispatch({
      type: CAMPAIGN_LIST_GET_ALL,
    })
    const syncAtStart = pendingCampaignRecipientSync
    let syncFinished = false
    void syncAtStart.then(() => {
      syncFinished = true
    })
    // a list edit may still be re-saving recipients, which would show stale counts
    await waitForCampaignRecipientSync()

    try {
      const campaigns = await fetchAllCampaigns()

      dispatch({
        type: CAMPAIGN_LIST_GET_ALL_SUCCESS,
        campaigns,
      })

      // the sync outlived the wait, so this list may predate its saves
      if (!syncFinished) {
        await syncAtStart
        try {
          dispatch({
            type: CAMPAIGN_LIST_GET_ALL_SUCCESS,
            campaigns: await fetchAllCampaigns(),
          })
        } catch (error) {
          // the first load already succeeded, so a failed refresh must not mark the list as failed
          captureError(error, {
            description: 'Error refreshing campaigns after recipient sync',
          })
        }
      }
    } catch (error) {
      dispatch({
        type: CAMPAIGN_LIST_GET_ALL_FAIL,
        error,
      })
    }
  }
}
/**
 * Loads a single campaign by ID.
 * @param campaignId - ID of the campaign to load
 * @returns Thunk that fetches the specific campaign
 */
export function campaignListGetSingle(campaignId: EmailCampaignId): Thunk {
  return async dispatch => {
    dispatch({
      type: CAMPAIGN_LIST_GET,
      campaignId,
    })

    try {
      const campaign = await api.getCampaign(campaignId)
      dispatch({
        type: CAMPAIGN_LIST_GET_SUCCESS,
        campaignId,
        campaign,
      })
    } catch (error) {
      dispatch({
        type: CAMPAIGN_LIST_GET_FAIL,
        campaignId,
        error,
      })
    }
  }
}
/**
 * Filters the campaign list by campaign type.
 * @param campaignType - Type of campaign to filter by or null for all
 * @returns Action object to filter campaign list
 */
export function campaignListFilter(
  campaignType: null | 'campaign' | 'briefing',
): {
  campaignType: null | 'campaign' | 'briefing'
  type: string
} {
  return {
    type: CAMPAIGN_LIST_FILTER,
    campaignType,
  }
}
/**
 * Begins the campaign creation process by opening the creation modal.
 * @param campaignType - Type of campaign to create ('campaign' or 'briefing')
 * @returns Action object to open campaign creation modal
 */
export function campaignListBeginCreating(
  campaignType: 'campaign' | 'briefing',
): {
  campaignType: 'campaign' | 'briefing'
  type: string
} {
  // opens modal
  return {
    type: CAMPAIGN_LIST_BEGIN_CREATING,
    campaignType,
  }
}
/**
 * Cancels the campaign creation process.
 * @returns Action object to cancel campaign creation
 */
export function campaignListCancelCreating(): {
  type: string
} {
  return {
    type: CAMPAIGN_LIST_CANCEL_CREATING,
  }
}
/**
 * Updates fields in the campaign creation/edit modal.
 * @param updatedFields - Partial campaign fields to update
 * @returns Action object to update modal fields
 */
export function campaignListModalUpdate(
  updatedFields: Partial<EmailCampaign>,
): {
  type: string
  updatedFields: Partial<EmailCampaign>
} {
  // modal editing
  return {
    type: 'CAMPAIGN_LIST_MODAL_UPDATE',
    updatedFields: updatedFields,
  }
}
/**
 * Creates a new campaign with the provided information.
 * @param campaignInfo - Campaign information including name and type
 * @returns Thunk that creates the campaign and redirects to editor
 */
export function campaignListCreate(campaignInfo: {
  name: string
  type: 'briefing' | 'campaign'
}): Thunk {
  return async dispatch => {
    try {
      dispatch({
        type: CAMPAIGN_LIST_CREATE,
        campaignInfo,
      })
      const campaign = await api.createCampaign({
        ...campaignInfo,
        status: 'draft',
        recipients_list: [],
        recipients_count: 0,
        recipients_filter: null,
        sender_name: '',
        sender_email: '',
        template_data: {
          subject: '',
          template_content_saved: false,
          campaignEditor: {
            sections: defaultCampaignTemplate.layout as CampaignSection[],
          },
        },
        template: defaultCampaignTemplate.id,
        scheduled_at: null,
        scheduled_at_timezone: null,
      })
      const { type } = campaign
      pushUINotification({
        message: `${upperFirst(type)} created!`,
        // message: 'Campaign created!',
        // message: 'Briefing created!',
        level: 'success',
      })
      mixpanel.sendPureEvent('Email Creator - Create new email')
      dispatch({
        type: CAMPAIGN_LIST_CREATE_SUCCESS,
        campaign,
      })
      NavigationService.push(getCampaignLink(campaign))
    } catch (error) {
      dispatch({
        type: CAMPAIGN_LIST_CREATE_FAIL,
        error,
      })
      pushUINotification({
        message: parseServerError(error.body),
        level: 'error',
      })
    }
  }
}

function duplicateName(name: string) {
  // converts:
  //   - foo -> foo copy
  //   - foo copy -> foo copy 2
  //   - foo copy 2 -> foo copy 3
  const copy = /(.*) copy( \d+)?$/.exec(name)

  if (copy && copy[2]) {
    const number = parseInt(copy[2]) || 1
    return `${copy[1]} copy ${number + 1}`
  } else {
    return `${name} copy`
  }
}

/**
 * Duplicates an existing campaign with a new name.
 * @param campaignId - ID of the campaign to duplicate
 * @returns Thunk that duplicates the campaign and redirects to editor
 */
export function campaignListDuplicate(
  campaignId: EmailCampaignId,
): Record<string, any> | null | undefined {
  return async (dispatch, getState) => {
    try {
      await dispatch(campaignListGetSingle(campaignId))
      const origCampaign = getCampaign(getState(), {
        campaignId: campaignId,
      })
      dispatch({
        type: CAMPAIGN_LIST_CREATE,
        campaignInfo: origCampaign,
      })
      let destCampaign = await api.createCampaign({
        ...origCampaign,
        scheduled_at: null,
        scheduled_at_timezone: null,
        sent_at: null,
        name: duplicateName(
          origCampaign.name || `Untitled ${origCampaign.type}`,
        ),
      })
      destCampaign = {
        justDuplicated: true,
        ...destCampaign,
      }
      dispatch({
        type: CAMPAIGN_LIST_CREATE_SUCCESS,
        campaign: destCampaign,
      })
      NavigationService.push(getCampaignLink(destCampaign))
      return destCampaign
    } catch (error) {
      dispatch({
        type: CAMPAIGN_LIST_CREATE_FAIL,
        campaignId,
        error,
      })
      pushUINotification({
        message: parseServerError(error.body),
        level: 'error',
      })
      return null
    }
  }
}
/**
 * Deletes a campaign by ID.
 * @param campaignId - ID of the campaign to delete
 * @returns Thunk that deletes the campaign
 */
export function campaignListDelete(campaignId: EmailCampaignId): Thunk {
  return async dispatch => {
    try {
      dispatch({
        type: CAMPAIGN_LIST_DELETE,
        campaignId,
      })
      await api.deleteCampaign(campaignId)
      dispatch({
        type: CAMPAIGN_LIST_DELETE_SUCCESS,
        campaignId,
      })
      mixpanel.sendPureEvent('Email Creator - Delete email')
    } catch (error) {
      dispatch({
        type: CAMPAIGN_LIST_DELETE_FAIL,
        campaignId,
        error,
      })
    }
  }
}
/**
 * Begins editing a campaign by loading all necessary data and initializing forms.
 * @param campaignId - ID of the campaign to begin editing
 * @returns Thunk that loads campaign data and initializes editor state
 */
export function campaignListBeginEdit(campaignId: EmailCampaignId): Thunk {
  // init all forms
  return async (dispatch, getState) => {
    let campaign = getCampaign(getState(), {
      campaignId: campaignId,
    })

    // an exit save still running may come back with a conflict, which the check below must see
    if (campaign?.saving) {
      await waitForPendingSave(campaignId)
      campaign = getCampaign(getState(), { campaignId })
    }

    // the editor is about to be reset to the last saved content, so "Keep my changes" would save stale content
    if (campaign?.conflicting) {
      dispatch({
        type: CAMPAIGN_DETAILS_RESOLVE_CONFLICT,
        campaignId,
        option: 'CONFLICT_LOAD_LATEST',
      })
      campaign = getCampaign(getState(), { campaignId })
    }

    // a list refresh keeps the stored detail fields, so only a refetch picks up edits made elsewhere
    // unsaved or in-flight edits would be overwritten by it
    const hasLocalEdits = campaign?.dirty || campaign?.saving
    // started before the refetch so the stale editor isn't left showing while footers load after it
    const footersLoaded = dispatch(loadCampaignFooters())
    if (!campaign || !campaign.recipients_list || !hasLocalEdits) {
      await dispatch(campaignListGetSingle(campaignId))
    }
    // footers must be in the store before the editor becomes interactive
    await footersLoaded

    campaign = getCampaign(getState(), {
      campaignId: campaignId,
    })
    const template_data = getTemplateData(campaign)
    const recipientRules = getRecipientFilter(campaign)
    const { campaignEditor } = template_data

    dispatch({
      type: CAMPAIGN_LIST_BEGIN_EDIT,
      campaign,
      campaignEditor,
      recipientRules,
    })
  }
}
/**
 * Updates the search query for filtering campaigns.
 * @param q - Search query string or null to clear
 * @returns Action object to update campaign search query
 */
export function updateCampaignSearchQuery(q: string | null | undefined): {
  type: string
  q: string | null | undefined
} {
  return {
    type: CAMPAIGN_SEARCH,
    q,
  }
}
